import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import Svg, { Circle } from 'react-native-svg';
import { COLORS, FONTS } from '../constants/config';
import { KPIDistribution } from '../types';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { formatPct } from '../utils/formatPercentage';

interface KPIDistributionCardProps {
  distribution: KPIDistribution | undefined;
  title?: string;
}

/**
 * DistributionDonut - Multi-segment donut chart for KPI distribution
 */
const DistributionDonut = ({
  excellent,
  good,
  needsImprovement,
  size = 144,
  strokeWidth = 14,
}: {
  excellent: number;
  good: number;
  needsImprovement: number;
  size?: number;
  strokeWidth?: number;
}) => {
  const { colors, isDark } = useTheme();
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;

  // Calculate segment lengths
  const excellentLength = (excellent / 100) * circumference;
  const goodLength = (good / 100) * circumference;
  const needsImprovementLength = (needsImprovement / 100) * circumference;

  // Calculate offsets for each segment
  const excellentOffset = circumference - excellentLength;
  const goodOffset = excellentOffset - goodLength;
  const needsImprovementOffset = goodOffset - needsImprovementLength;

  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={size} height={size}>
        {/* Background circle */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={isDark ? '#374151' : '#E2E8F0'}
          strokeWidth={strokeWidth}
          fill="none"
        />

        {/* Excellent segment (Green) */}
        {excellent > 0 && (
          <Circle
            cx={center}
            cy={center}
            r={radius}
            stroke="#22C55E"
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={`${excellentLength} ${circumference}`}
            strokeDashoffset={excellentOffset}
            strokeLinecap="round"
            transform={`rotate(-90 ${center} ${center})`}
          />
        )}

        {/* Good segment (Orange) */}
        {good > 0 && (
          <Circle
            cx={center}
            cy={center}
            r={radius}
            stroke="#F97316"
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={`${goodLength} ${circumference}`}
            strokeDashoffset={goodOffset}
            strokeLinecap="round"
            transform={`rotate(-90 ${center} ${center})`}
          />
        )}

        {/* Needs Improvement segment (Red) */}
        {needsImprovement > 0 && (
          <Circle
            cx={center}
            cy={center}
            r={radius}
            stroke="#EF4444"
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={`${needsImprovementLength} ${circumference}`}
            strokeDashoffset={needsImprovementOffset}
            strokeLinecap="round"
            transform={`rotate(-90 ${center} ${center})`}
          />
        )}
      </Svg>
    </View>
  );
};

/**
 * KPIDistributionCard - Displays KPI achievement distribution
 * Categories:
 * - ممتاز (Excellent): achievement >= 100% (Green)
 * - جيد (Good): achievement >= 80% and < 100% (Orange)
 * - يحتاج تحسين (Needs Improvement): achievement < 80% (Red)
 */
export default function KPIDistributionCard({ distribution, title }: KPIDistributionCardProps) {
  const { colors, isDark } = useTheme();
  const { t } = useLanguage();

  // Use provided title or default to translated value
  const displayTitle = title ?? t('kpis.distribution');

  const hasData = distribution && distribution.total > 0;

  const categories = [
    {
      label: t('kpis.excellent'),
      percentage: hasData ? distribution.excellent : 0,
      count: hasData ? distribution.excellent_count : 0,
      color: '#22C55E', // Green
    },
    {
      label: t('kpis.good'),
      percentage: hasData ? distribution.good : 0,
      count: hasData ? distribution.good_count : 0,
      color: '#F97316', // Orange
    },
    {
      label: t('kpis.needsImprovement'),
      percentage: hasData ? distribution.needs_improvement : 0,
      count: hasData ? distribution.needs_improvement_count : 0,
      color: '#EF4444', // Red
    },
  ];

  return (
    <View style={[styles.container, { backgroundColor: colors.card, borderColor: colors.cardBorder }]}>
      {/* Title */}
      <View style={styles.titleContainer}>
        <Text style={[styles.title, { color: colors.textPrimary }]}>{displayTitle}</Text>
      </View>

      {/* Distribution Content */}
      <View style={styles.contentContainer}>
        {/* Left side - Donut chart */}
        <View style={styles.chartPlaceholder}>
          <DistributionDonut
            excellent={hasData ? distribution.excellent : 0}
            good={hasData ? distribution.good : 0}
            needsImprovement={hasData ? distribution.needs_improvement : 0}
            size={144}
            strokeWidth={14}
          />
        </View>

        {/* Right side - Categories */}
        <View style={styles.categoriesContainer}>
          {categories.map((category, index) => (
            <View key={index} style={styles.categoryItem}>
              {/* Color indicator bar */}
              <View style={[styles.colorDot, { backgroundColor: category.color }]} />

              {/* Label */}
              <Text style={[styles.categoryLabel, { color: colors.textPrimary }]}>
                {category.label}
              </Text>

              {/* Percentage badge */}
              <View style={[styles.countBadge, { backgroundColor: isDark ? '#374151' : '#F1F5F9' }]}>
                <Text style={[styles.countText, { color: colors.textPrimary }]}>
                  %{formatPct(category.percentage)}
                </Text>
              </View>
            </View>
          ))}
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    padding: 20,
    height: '100%',
    elevation: 2,
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
  },
  titleContainer: {
    marginBottom: 20,
  },
  title: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  contentContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 20,
  },
  // Donut chart container
  chartPlaceholder: {
    width: 144,
    height: 144,
    alignItems: 'center',
    justifyContent: 'center',
  },
  categoriesContainer: {
    flex: 1,
    gap: 24,
  },
  categoryItem: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 8,
    height: 32,
  },
  countBadge: {
    backgroundColor: '#F1F5F9',
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 100,
    minWidth: 48,
    alignItems: 'center',
    justifyContent: 'center',
  },
  countText: {
    fontSize: 16,
    fontFamily: FONTS.numbers,
    fontWeight: '500',
    color: '#414042',
    textAlign: 'center',
  },
  categoryLabel: {
    flex: 1,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  colorDot: {
    width: 8,
    height: 32,
    borderRadius: 4,
  },
});
